"use client";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import api from "../api"; // Use the central API instance
import categoryFilterMatrix from "./categoryFilterMatrix.json";

const createDefaultFilters = () => ({
  brands: [],
  colors: [],
  sizes: [],
  types: [],
  customizable: [],
  gender: [],
  safety_compliance: [],
  style_fit: [],
  material: [],
  features_performance: [],
  weather_protection: [],
  availability: [],
  min_price: 0,
  max_price: 1000,
  product_filters: {},
});

const CategoryPage = () => {
  useEffect(() => {
    document.title = "Category Products | Workgear365";
  }, []);

  const { type, name } = useParams();
  const categoryIdentity = `${type}:${name}`;
  const activeCategoryRef = useRef(categoryIdentity);
  activeCategoryRef.current = categoryIdentity;
  const productRequestRef = useRef(0);
  const [filterCategoryIdentity, setFilterCategoryIdentity] = useState(categoryIdentity);
  const [parentMenu, setParentMenu] = useState(type === "main_menu" ? name : "");
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  // State
  const [products, setProducts] = useState([]);
  const [groupedColors, setGroupedColors] = useState({});
  const [brands, setBrands] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [types, setTypes] = useState([]);
  const [availableProductFilters, setAvailableProductFilters] =
  useState({});
  const normalizeCategoryKey = (value) =>
    String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  const exactCategoryFilterConfig = categoryFilterMatrix.find((entry) =>
    entry.route
      && normalizeCategoryKey(entry.route.type) === normalizeCategoryKey(type)
      && normalizeCategoryKey(entry.route.name) === normalizeCategoryKey(name)
  );
  const categoryFilterConfig = exactCategoryFilterConfig || categoryFilterMatrix.find((entry) =>
    normalizeCategoryKey(entry.menu) === normalizeCategoryKey(parentMenu)
      && normalizeCategoryKey(entry.page).startsWith("shop all ")
  );
  const configuredFilters = categoryFilterConfig?.filters || [];
  const hasConfiguredFilter = (label) =>
    configuredFilters.some((filter) =>
      normalizeCategoryKey(filter) === normalizeCategoryKey(label)
    );
  const filterOrder = (label) => {
    const index = configuredFilters.findIndex((filter) =>
      normalizeCategoryKey(filter) === normalizeCategoryKey(label)
    );
    return index < 0 ? undefined : index;
  };

  useEffect(() => {
    if (type === "main_menu") {
      setParentMenu(name);
      return;
    }

    setParentMenu("");

    if (type !== "Category" && type !== "subcategory") {
      return;
    }

    let isCurrentPage = true;
    const breadcrumbType = type === "Category" ? "category" : "subcategory";
    api.get(`/api/breadcrumb/${breadcrumbType}/${encodeURIComponent(name)}`)
      .then((response) => {
        if (isCurrentPage) setParentMenu(response.data?.main_menu || "");
      })
      .catch(() => {
        if (isCurrentPage) setParentMenu("");
      });

    return () => {
      isCurrentPage = false;
    };
  }, [type, name]);
  const [view, setView] = useState("grid");
  const [mainColors, setMainColors] = useState([]);
  const [sortBy, setSortBy] = useState("recommended");
  const [showAllColors, setShowAllColors] = useState(false);
  const [showAll, setShowAll] = useState({
    brands: false,
    sizes: false,
  });
  
  const [filters, setFilters] = useState(createDefaultFilters);
  // The matcher may return either grouped options or its raw match list.
  // Normalize both formats so only filters found on products in this category
  // are rendered.
  const normalizeAvailableProductFilters = (availableFilters) => {
    if (Array.isArray(availableFilters)) {
      return availableFilters.reduce((groups, match) => {
        if (!match?.filter_name || !match?.filter_value) return groups;
        groups[match.filter_name] ||= [];
        if (!groups[match.filter_name].includes(match.filter_value)) {
          groups[match.filter_name].push(match.filter_value);
        }
        return groups;
      }, {});
    }

    if (!availableFilters || typeof availableFilters !== "object") return {};

    return Object.fromEntries(
      Object.entries(availableFilters).map(([filterName, options]) => [
        filterName,
        Array.isArray(options)
          ? [...new Set(options.map((option) =>
              typeof option === "string" ? option : option?.filter_value ?? option?.value ?? option?.name
            ).filter(Boolean))]
          : [],
      ])
    );
  };
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
const [tempPrice, setTempPrice] = useState({
    min: filters.min_price,
    max: filters.max_price,
  });

  useEffect(() => {
    productRequestRef.current += 1;
    setFilterCategoryIdentity(categoryIdentity);
    setFilters(createDefaultFilters());
    setMainColors([]);
    setPage(1);
    setTotalPages(1);
    setTempPrice({ min: 0, max: 1000 });
    setShowAllColors(false);
    setShowAll({ brands: false, sizes: false });
    setBrands([]);
    setSizes([]);
    setTypes([]);
    setGroupedColors({});
    setProducts([]);
    setAvailableProductFilters({});
  }, [categoryIdentity]);

  // Fetch filters
  const fetchFilters = useCallback(async () => {
    const requestCategory = categoryIdentity;
    try {
      const [brandsRes, colorsRes, sizesRes,typesRes] = await Promise.all([
        api.get(`/api/filterbrands/${name}/${type}`),
        api.get(`/api/filtercolors/${name}/${type}`),
        api.get(`/api/filtersizes/${name}/${type}`),
        api.get(`/api/filtertypes/${type}/${name}`),
      ]);

      if (activeCategoryRef.current !== requestCategory) return;
     
      const normalize = (data) =>
        Array.isArray(data)
          ? data.map((item) => (typeof item === "string" ? item : item.name))
          : [];

      setBrands(brandsRes.data);
      setSizes(sizesRes.data);
      setTypes(typesRes.data);
      // handle grouped color data like brandpage
      
      setGroupedColors(colorsRes.data || {});
    } catch (err) {
      if (activeCategoryRef.current === requestCategory) {
        console.error("Error fetching filters:", err);
      }
    }
  }, [name, type, categoryIdentity]);

  // Fetch products
  const fetchProducts = useCallback(async () => {
    const requestCategory = categoryIdentity;
    if (filterCategoryIdentity !== requestCategory) return;

    const requestId = ++productRequestRef.current;
    setLoading(true);
    try {
      const res = await api.get(`/api/products/${type}/${name}`, {
        params: { 
          ...filters,
          main_colors: mainColors,
          sort_by: sortBy,
          page 
        },
      });
      if (activeCategoryRef.current !== requestCategory || requestId !== productRequestRef.current) return;
      setProducts(res.data.data?.data || []);
      setAvailableProductFilters(
        normalizeAvailableProductFilters(res.data.available_filters)
      );
      setTotalPages(res.data.data?.last_page || 1);
    } catch (err) {
      if (activeCategoryRef.current === requestCategory && requestId === productRequestRef.current) {
        console.error("Error fetching products:", err);
      }
    } finally {
      if (activeCategoryRef.current === requestCategory && requestId === productRequestRef.current) {
        setLoading(false);
      }
    }
  }, [type, name, categoryIdentity, filterCategoryIdentity, filters, page, sortBy, mainColors]);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);
  //mobile filter
  useEffect(() => {
      if (mobileFilterOpen) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }
    }, [mobileFilterOpen]);
  // Handlers
  const handleFilterChange = (type, value) => {
    setFilters((prev) => ({
      ...prev,
      [type]: prev[type].includes(value)
        ? prev[type].filter((v) => v !== value)
        : [...prev[type], value],
    }));
     if (window.innerWidth < 992) {
      setMobileFilterOpen(false);
    }
  };
  const handleProductFilterChange = (filterKey, value) => {
  setFilters((prev) => {
    const currentValues = prev.product_filters?.[filterKey] || [];

    const updatedValues = currentValues.includes(value)
      ? currentValues.filter((item) => item !== value)
      : [...currentValues, value];

    return {
      ...prev,
      product_filters: {
        ...prev.product_filters,
        [filterKey]: updatedValues,
      },
    };
  });

  setPage(1);

  if (window.innerWidth < 992) {
    setMobileFilterOpen(false);
  }
};
  const handlePriceChange = (minOrMax, value) => {
    setFilters((prev) => ({ ...prev, [minOrMax]: value }));
  };

  const clearFilters = () => {
  setFilters({
    brands: [],
    colors: [],
    sizes: [],
    types: [],
    customizable: [],
    gender: [],
    safety_compliance: [],
    style_fit: [],
    material: [],
    features_performance: [],
    weather_protection: [],
    availability: [],
    min_price: 0,
    max_price: 1000,
    product_filters: {},
  });
  
  setMainColors([]);
  setPage(1);

  setTempPrice({
    min: 0,
    max: 1000,
  });
};

  const slugify = (str) =>
    str.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)+/g, "");

  const removeColor = (color) => {
    setFilters((prev) => ({
      ...prev,
      colors: prev.colors.filter((c) => c !== color),
    }));
  };
     
  const renderPagination = () => {
    const pageNumbers = [];
    const totalNumbersToShow = 5;
    let startPage = Math.max(1, page - Math.floor(totalNumbersToShow / 2));
    let endPage = Math.min(totalPages, startPage + totalNumbersToShow - 1);

    if (endPage - startPage < totalNumbersToShow - 1) {
      startPage = Math.max(1, endPage - totalNumbersToShow + 1);
    }

    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) pageNumbers.push("...");
    }

    for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) pageNumbers.push("...");
      pageNumbers.push(totalPages);
    }

    return pageNumbers.map((p, idx) =>
      p === "..." ? (
        <span key={idx} className="btn btn-sm mx-1 disabled">
          ...
        </span>
      ) : (
        <button
          key={idx}
          className={`btn btn-sm mx-1 ${
            page === p ? "btn-primary" : "btn-outline-primary"
          }`}
          onClick={() => setPage(p)}
        >
          {p}
        </button>
      )
    );
  };
  // ------------------------------------
      //  DUAL SLIDER COMPONENT
      // ------------------------------------
    const DualSlider = ({ min, max }) => {
      const [minVal, setMinVal] = useState(filters.min_price);
      const [maxVal, setMaxVal] = useState(filters.max_price);
    
      const updateFilters = (newMin, newMax) => {
        setFilters((prev) => ({
          ...prev,
          min_price: newMin,
          max_price: newMax,
        }));
      };
    
      const handleMin = (e) => {
        const value = Math.min(Number(e.target.value), maxVal - 1);
        setMinVal(value);
        updateFilters(value, maxVal);
      };
    
      const handleMax = (e) => {
        const value = Math.max(Number(e.target.value), minVal + 1);
        setMaxVal(value);
        updateFilters(minVal, value);
      };
    
      // CLICK ON TRACK → JUMP THUMB
      const handleTrackClick = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const percent = clickX / rect.width;
        const value = Math.round(percent * max);
    
        // Move nearest thumb
        if (Math.abs(value - minVal) < Math.abs(value - maxVal)) {
          const newMin = Math.min(value, maxVal - 1);
          setMinVal(newMin);
          updateFilters(newMin, maxVal);
        } else {
          const newMax = Math.max(value, minVal + 1);
          setMaxVal(newMax);
          updateFilters(minVal, newMax);
        }
      };
    
      return (
        <div className="dual-slider">
          {/* CLICKABLE TRACK */}
          <div className="slider-click-area" onClick={handleTrackClick}>
            <div className="slider-track"></div>
    
            <div
              className="slider-range"
              style={{
                left: `${(minVal / max) * 100}%`,
                width: `${((maxVal - minVal) / max) * 100}%`,
              }}
            ></div>
          </div>
    
          {/* SLIDER INPUTS (ON TOP BUT DO NOT BLOCK CLICKS) */}
          <input
            type="range"
            min={min}
            max={max}
            value={minVal}
            onChange={handleMin}
            className="thumb-left"
          />
    
          <input
            type="range"
            min={min}
            max={max}
            value={maxVal}
            onChange={handleMax}
            className="thumb-right"
          />
    
          <div className="d-flex justify-content-between small mt-1">
            <span>${minVal}</span>
            <span>{maxVal === 1000 ? "$1000+" : `$${maxVal}`}</span>
          </div>
        </div>
      );
    };
  const renderCheckboxFilter = (items, type) => {
  const visibleItems = showAll[type] ? items : items.slice(0, 5);

  return (
    <div className="wg-plp-filter-body">
      {visibleItems.map((item, idx) => (
        <label key={idx}>
          <input
            type="checkbox"
            checked={filters[type].includes(item.name)}
            onChange={() => { handleFilterChange(type, item.name);  if (window.innerWidth < 992) setMobileFilterOpen(false);}}
          />
          <span className="wg-plp-filter-text">{item.name}</span>
          <span className="wg-plp-filter-count">({item.count})</span>
           
        </label>
      ))}

      {items.length > 5 && (
        <button
          type="button"
          className="wg-plp-show-more"
          onClick={() =>
            setShowAll((prev) => ({ ...prev, [type]: !prev[type] }))
          }
        >
          {showAll[type] ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
};
const renderDynamicProductFilters = () => {
  const grouped = Object.entries(availableProductFilters).reduce((groups, [filterName, rawOptions]) => {
    const aliases = {
      "Headwear Type": ["Style"],
      "Bag Type": ["Style"],
      "Thermal Features": ["Style"],
      "Safety & Compliance": ["Safety & Standards"],
      "Footwear Features": ["Safety & Standards"],
      "Eyewear Features": ["Safety & Standards"],
      "Glove Features": ["Safety & Standards"],
    };
    const labels = [filterName, ...(aliases[filterName] || [])];
    const configuredLabels = configuredFilters
      ? configuredFilters.filter((label) => labels.some((alias) => normalizeCategoryKey(alias) === normalizeCategoryKey(label)))
      : [filterName];
    configuredLabels.forEach((displayName) => {
      groups[displayName] ||= [];
      (Array.isArray(rawOptions) ? rawOptions : []).forEach((option) => {
        groups[displayName].push({ filterName, option });
      });
    });
    return groups;
  }, {});

  const displayNames = Object.keys(grouped).sort((a, b) => filterOrder(a) - filterOrder(b));
  return displayNames.map((displayName) => (
    <details key={displayName} data-filter-first={filterOrder(displayName) === 0 ? "true" : "false"} data-filter-group={normalizeCategoryKey(displayName).replace(/\s+/g, "_")} style={{ order: filterOrder(displayName) }}>
      <summary>{displayName}</summary>
      <div className="wg-plp-filter-body">
        {grouped[displayName].map(({ filterName, option }) => {
          const filterKey = filterName
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "_")
            .replace(/^_+|_+$/g, "");
          const selectedValues = filters.product_filters?.[filterKey] || [];
          return (
            <label key={`${filterKey}-${option}`}>
              <input
                type="checkbox"
                checked={selectedValues.includes(option)}
                onChange={() => handleProductFilterChange(filterKey, option)}
              />
              <span className="wg-plp-filter-text">{option}</span>
            </label>
          );
        })}
      </div>
    </details>
  ));
};
const Swatches = ({ variants = [] }) => { 
  const [expanded, setExpanded] = useState(false);

  // Build unique colors from variants
  const unique = [];
  const map = new Map();
  
  variants.forEach((v) => { 
    if (v.colorName && v.color_code && !map.has(v.colorName)) {
      map.set(v.colorName, {
        name: v.colorName,
        hex: v.color_code,
      });
      unique.push({
        name: v.colorName,
        hex: v.color_code,
      });
    }
  });

  if (!unique.length) return null;

  const visible = expanded ? unique : unique.slice(0, 4);
  const remaining = unique.length - 4;

  return (
    <div className="wg-plp-color-row" role="group">
      {visible.map((s, i) => (
        <button
          key={i}
          type="button"
          className="wg-plp-swatch"
          title={s.name}
          style={{ "--swatch-color" : s.hex }}
        />
      ))}

      {!expanded && remaining > 0 && (
        <button
          type="button"
          className="wg-plp-swatch-more"
          onClick={() => setExpanded(true)}
        >
          +{remaining}
        </button>
      )}
    </div>
  );
};
  return (
    <div className="wg-plp">
  <div className="wg-plp-header">
    <div className="wg-plp-hero-desc">
      <h1 className="wg-plp-hero-headline">{name}</h1>
      
      {types.length > 0 && (
          <div className="wg-plp-hero-pills">
            <span className="wg-plp-hero-pills-label">Shop By Type:</span>

            {types.map((t) => (
                <a
                  key={t.id}
                  href="#"
                  className={`wg-plp-hero-pill ${
                    filters.types.includes(t.id) ? "is-active" : ""
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleFilterChange("types", t.id);
                     if (window.innerWidth < 992) setMobileFilterOpen(false);
                  }}
                >
                  {t.name}
                </a>
              ))}
          </div>
       )}
    </div>

  </div>

  <div className="wg-plp-mobile-controls">
    <div className="wg-plp-mobile-controls-inner">
      <button type="button" className="wg-plp-btn" onClick={() => setMobileFilterOpen(true)} >
          Filters
        </button>
    </div>
  </div>
  <div
    className={`wg-plp-filter-backdrop ${mobileFilterOpen ? "is-visible" : ""}`}
    onClick={() => setMobileFilterOpen(false)}
  />

  <div className="wg-plp-layout">
    <aside className={`wg-plp-sidebar ${mobileFilterOpen ? "is-open" : ""}`}>
      <div className="wg-plp-filter-card">
        
        <div className="wg-plp-filter-title">
          <div className="wg-plp-mobile-close">
            <button onClick={() => setMobileFilterOpen(false)}>✕ Close</button>
          </div>
          <div className="wg-plp-filter-title-left">
              <span>Filter By</span>
              <span style={{ fontSize: "0.8rem", color: "#666" }} id="wgActiveFiltersLabel" >All Products</span>
          </div>
          <button type="button" className="wg-plp-btn-link" id="wgResetFiltersBtn" onClick={clearFilters}>Clear</button>
        </div>

        <div className="wg-plp-chips" id="wgActiveChips"></div>

        <div className="wg-plp-filter-group wg-plp-filter-group--matrix" style={configuredFilters ? { display: "flex", flexDirection: "column" } : undefined}>
          
           {(hasConfiguredFilter("Product Type") || hasConfiguredFilter("Type")) && types.length > 0 && (
              <details data-filter-first={filterOrder(configuredFilters?.includes("Product Type") ? "Product Type" : "Type") === 0 ? "true" : "false"} data-filter-group="type" style={{ order: filterOrder(configuredFilters?.includes("Product Type") ? "Product Type" : "Type") }}>
                <summary>Type</summary>
                <div className="wg-plp-filter-body">
                  {types.map((t) => (
                    <label key={t.id}>
                      <input
                        type="checkbox"
                        checked={filters.types.includes(t.id)}
                        onChange={() => { handleFilterChange("types", t.id); if (window.innerWidth < 992) setMobileFilterOpen(false); }}
                      />
                      {t.name}
                    </label>
                  ))}
                </div>
              </details>
            )}
          

         {/* Safety & Visibility */}
       {/* <details data-filter-group="safety">
          <summary>Safety &amp; Visibility</summary>
          <div className="wg-plp-filter-body" data-filter-body="safety">
            {Object.keys(groupedColors).map((main) => (
              <label key={main}>
                <input
                  type="checkbox"
                  value={main}
                  checked={mainColors.includes(main)}
                  onChange={() => {
                        const itsColors = groupedColors[main] || [];

                        setMainColors((prev) =>
                          prev.includes(main)
                            ? prev.filter((m) => m !== main)
                            : [...prev, main]
                        );

                        setFilters((prev) => {
                          const isChecked = mainColors.includes(main);

                          return {
                            ...prev,
                            colors: isChecked
                              ? prev.colors.filter((c) => !itsColors.includes(c))
                              : [...new Set([...prev.colors, ...itsColors])],
                          };
                        });
                      }}
                />{" "}
                {main}
              </label>
            ))}
          </div>
        </details> */}
        
        {/* Color */}
       {/* <details data-filter-group="color">
          <summary>Color</summary>
          <div className="wg-plp-filter-body" data-filter-body="color">
           {Object.entries(groupedColors).map(([main, colorList]) =>
              colorList.map((c) => (
                <label key={main + c}>
                  <input
                    type="checkbox"
                    checked={filters.colors.includes(c)}
                    onChange={() => handleFilterChange("colors", c)}
                  />
                  {c}
                </label>
              ))
            )}

            <a href="#" className="wg-plp-show-more">Show more</a>
          </div>
        </details>
        */}
        {/* Color */}
       {hasConfiguredFilter("Color") && (Array.isArray(groupedColors) ? groupedColors.length > 0 : Object.values(groupedColors).some((colors) => colors?.length > 0)) && <details data-filter-first={filterOrder("Color") === 0 ? "true" : "false"} data-filter-group="color" style={{ order: filterOrder("Color") }}>
            <summary>Color</summary>

            <div className="wg-plp-filter-body" data-filter-body="color">

              {(() => {
                // flatten colors from groupedColors
                const allColors = Array.isArray(groupedColors)
                  ? groupedColors
                  : Object.values(groupedColors).flat();

                const visibleColors = showAllColors ? allColors : allColors.slice(0, 5);

                return (
                  <>
                    {visibleColors.map((color, idx) => (
                      <label key={idx}>
                        <input
                          type="checkbox"
                          checked={filters.colors.includes(color)}
                          onChange={() => { handleFilterChange("colors", color); if (window.innerWidth < 992) setMobileFilterOpen(false); }}
                        />
                        {color}
                      </label>
                    ))}

                    {allColors.length > 5 && (
                      <button
                        type="button"
                        className="wg-plp-show-more"
                        onClick={() => setShowAllColors((prev) => !prev)}
                      >
                        {showAllColors ? "Show less" : "Show more"}
                      </button>
                    )}
                  </>
                );
              })()}

            </div>
          </details>}
          {hasConfiguredFilter("Size") && sizes.length > 0 && <details data-filter-first={filterOrder("Size") === 0 ? "true" : "false"} data-filter-group="size" style={{ order: filterOrder("Size") }}>
            <summary>Size</summary>
            {renderCheckboxFilter(sizes, "sizes")}
          </details>}

          {hasConfiguredFilter("Brand") && brands.length > 0 && <details data-filter-first={filterOrder("Brand") === 0 ? "true" : "false"} data-filter-group="brand" style={{ order: filterOrder("Brand") }}>
            <summary>Brand</summary>
            {renderCheckboxFilter(brands, "brands")}
            
          </details>}

           {renderDynamicProductFilters()}
          {hasConfiguredFilter("Price") && <details data-filter-first={filterOrder("Price") === 0 ? "true" : "false"} data-filter-group="price" style={{ order: filterOrder("Price") }}>
            <summary>Price</summary>
            <div className="wg-plp-filter-body wg-plp-filter-body-price">
              <div className="wg-plp-price-slider">
                <div className="wg-plp-price-values">
                  <span>${filters.min_price}</span>
                  <span>${filters.max_price}</span>
                </div>
                <div className="wg-plp-price-slider-track">
                 <div className="wg-plp-price-range-fill"
                    style={{
                      left: `${(filters.min_price / 1000) * 100}%`,
                      width: `${((filters.max_price - filters.min_price) / 1000) * 100}%`,
                    }}
                  ></div>
                  <input
  type="range"
  min="0"
  max="1000"
  step="5"
  value={tempPrice.min}
  className="wg-plp-price-range"
  onChange={(e) =>
    setTempPrice((p) => ({ ...p, min: Number(e.target.value) }))
  }
  onMouseUp={() =>{
    setFilters((prev) => ({
      ...prev,
      min_price: tempPrice.min,
    }));
     if (window.innerWidth < 992) setMobileFilterOpen(false);
  }
  }
  onTouchEnd={() =>{
    setFilters((prev) => ({
      ...prev,
      min_price: tempPrice.min,
    }));
     if (window.innerWidth < 992) setMobileFilterOpen(false);
  }}
/>
<input
  type="range"
  min="0"
  max="1000"
  step="5"
  value={tempPrice.min}
  className="wg-plp-price-range"
  onChange={(e) =>
    setTempPrice((p) => ({ ...p, min: Number(e.target.value) }))
  }
  onMouseUp={() =>
    setFilters((prev) => ({
      ...prev,
      min_price: tempPrice.min,
    }))
  }
  onTouchEnd={() =>
    setFilters((prev) => ({
      ...prev,
      min_price: tempPrice.min,
    }))
  }
/>

    <input
      type="range"
      min="0"
      max="1000"
      step="5"
      value={tempPrice.max}
      className="wg-plp-price-range"
      onChange={(e) =>
        setTempPrice((p) => ({ ...p, max: Number(e.target.value) }))
      }
      onMouseUp={() =>
        setFilters((prev) => ({
          ...prev,
          max_price: tempPrice.max,
        }))
      }
      onTouchEnd={() =>
        setFilters((prev) => ({
          ...prev,
          max_price: tempPrice.max,
        }))
      }
    />
                </div>
              </div>
            </div>
          </details>}
        </div>

      </div>
    </aside>

    <section className={`wg-plp-results ${view === "list" ? "is-list" : ""}`}>
      <div className="wg-plp-results-header">
        <div className="wg-plp-count" id="wgProductCount"></div>
        <div className="wg-plp-header-actions">
        <div className="wg-plp-sort">
          <span>Sort by:</span>
          <select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value);
                setPage(1); // reset page on sort
              }}
            >
          {/*  <option value="recommended">Recommended</option>
            <option value="best_sellers">Best Sellers</option> */}
            <option value="newest">Newest</option>
            <option value="price_asc">Price: Low to High</option>
            <option value="price_desc">Price: High to Low</option>
            <option value="name_asc">Alphabetical (A-Z)</option>
            <option value="name_desc">Alphabetical (Z-A)</option>
          </select>
        </div>
          <div className="wg-plp-view-toggle" role="group" aria-label="View style">
            <button type="button" data-view="grid"  className={`wg-plp-view-btn ${view === "grid" ? "is-active" : ""}`}
    aria-pressed={view === "grid"}
    onClick={() => setView("grid")}>Grid</button>
            <button type="button"  className={`wg-plp-view-btn ${view === "list" ? "is-active" : ""}`}
    aria-pressed={view === "list"}
    onClick={() => setView("list")} data-view="list" >Compact List</button>
          </div>
        </div>
      </div>
      <div className="wg-plp-grid">
        {loading && <div className="loadingproduct">Loading...</div>}

        {!loading && products.length === 0 && (
          <div className="wg-plp-empty-title">
            No products match your filters.
          </div>
        )}

        {products.map((product, idx) => (
          <div className="wg-plp-card" key={idx}>
            
            <div className="wg-plp-card-img">
           {(() => {
                              const text = (
                                (product.description || "") + " " + (product.name || "")
                              ).toLowerCase();

                              // Ensure ANSI context exists
                              if (!text.includes("ansi")) return null;

                              // Find ALL "Class X"
                              const classMatches = [...text.matchAll(/class\s*(\d)/gi)];
                              const classes = [...new Set(classMatches.map(m => m[1]))];

                              if (classes.length) {
                                return (
                                  <span className="wg-plp-feature-badge">
                                    ANSI Class {classes.join(" / ")}
                                  </span>
                                );
                              }

                              return null;
                            })()}
              <div className="wg-plp-img-primary"> 
                <img
                  src={product.variants[0]?.frontimage}
                  alt={product.name}
                  className="wg-plp-img-tag"
                />
              </div>
              <div className="wg-plp-img wg-plp-img-secondary" style={{ backgroundImage: "url('112851_f_fm.jpg')" }}>
              </div>
              {/* <a className="wg-plp-quick-card-btn" href={`/products/${product.id}/${encodeURIComponent(product.name)}`}>Quick View</a> */}
            </div>
            

            <div className="wg-plp-card-brand">{product.Brand}</div>
            <div className="wg-plp-card-title">{product.name}</div>

            <div className="wg-plp-price-row">
              <div className="wg-plp-card-price">
                ${Number(product.variants[0]?.variantPrice).toFixed(2)}
              </div>
            </div>
            <div className="wg-plp-color-row" role="group" aria-label="Colors for ANSI Safety Cap">
               <Swatches variants={product.variants} />
            </div>
            <div className="wg-plp-card-footer">
              <a
                className="wg-plp-btn-cart"
                href={`/products/${product.id}/${encodeURIComponent(product.name)}`}
              >
                See Options
              </a>
            </div>
          </div>
        ))}
      </div>
      <div id="wgEmptyState" style={{display:"none",padding:"12px",border:"1px solid var(--wg-border)", borderRadius:"8px", background:"#fff"}}>
        <div className="wg-plp-empty-title">No products match your filters.</div>
        <div className="wg-plp-empty-meta" id="wgEmptySuggestion">Try removing a filter or view all headwear.</div>
        <div className="wg-plp-empty-actions">
          <button type="button" className="wg-plp-btn" id="wgEmptyResetBtn">View all headwear</button>
          <button type="button" className="wg-plp-btn wg-plp-btn-link" id="wgEmptyRemoveBtn">Remove most restrictive filter</button>
        </div>
      </div>
      <div className="d-flex justify-content-center mt-3">
            {renderPagination()}
          </div>
      
    </section>
  </div>
    </div>

  );
};

export default CategoryPage;
